import { apiFetch } from "./apiClient";

// ------------------------------------------------------------- Catalogue

export interface GuestServiceCatalogueItemDto {
  guestServiceItemId: number;
  itemName: string | null;
  description: string | null;
  isAvailable: boolean;
}

export interface GuestServiceCatalogueSectionDto {
  sectionName: string | null;
  items: GuestServiceCatalogueItemDto[] | null;
}

export interface GuestServiceCatalogueCategoryDto {
  guestServiceCategoryId: number;
  categoryCode: string | null;
  categoryName: string | null;
  description: string | null;
  iconName: string | null;
  displayOrder: number;
  allowsFreeTextRequest: boolean;
  freeTextLabel: string | null;
  freeTextInstructions: string | null;
  sections: GuestServiceCatalogueSectionDto[] | null;
}

/** The menu of non-food guest-service tabs as one booking sees it — mirrors
 * RoomServiceMenuDto's canOrder pattern: `canRequest` is false (with a
 * reason) when e.g. the stay hasn't started. */
export interface GuestServiceCatalogueDto {
  directBookingId: number;
  roomBookingId: number | null;
  branchId: number;
  roomNo: string | null;
  guestName: string | null;
  canRequest: boolean;
  cannotRequestReason: string | null;
  categories: GuestServiceCatalogueCategoryDto[] | null;
}

// ------------------------------------------------------------- Create (guest)

export interface CreateGuestServiceRequestLineDto {
  guestServiceItemId: number;
  quantity: number;
  notes: string | null;
}

export interface CreateGuestServiceRequestDto {
  directBookingId: number;
  roomBookingId: number | null;
  items: CreateGuestServiceRequestLineDto[] | null;
  notes: string | null;
  isUrgent: boolean;
  createdBy: number | null;
  guestPhone: string | null;
}

export interface CreateGuestServiceComplaintDto {
  directBookingId: number;
  roomBookingId: number | null;
  guestServiceCategoryId: number | null;
  complaintText: string | null;
  notes: string | null;
  isUrgent: boolean;
  createdBy: number | null;
  guestPhone: string | null;
}

// ------------------------------------------------------------- Request (admin)

export interface GuestServiceRequestDetailDto {
  requestDetailId: number;
  guestServiceItemId: number | null;
  itemName: string | null;
  sectionName: string | null;
  quantity: number;
  status: string | null;
  notes: string | null;
}

export interface GuestServiceRequestStatusHistoryDto {
  historyId: number;
  oldStatus: string | null;
  newStatus: string | null;
  changedOn: string;
  changedBy: number | null;
  source: string | null;
  remarks: string | null;
}

/** Maps to the backend's GuestServiceRequestDto — the real, live
 * GuestServiceRequests resource (a non-food guest request tied to a
 * DirectBooking/RoomBooking). */
export interface GuestServiceRequestDto {
  requestId: number;
  requestNumber: string | null;
  accountId: number;
  branchId: number;
  directBookingId: number;
  roomBookingId: number | null;
  roomId: number;
  roomNo: string | null;
  roomTypeId: number;
  roomTypeName: string | null;
  buildingId: number | null;
  customerId: number | null;
  guestName: string | null;
  guestServiceCategoryId: number | null;
  categoryName: string | null;
  categoryCode: string | null;
  /** "Cart" (item lines) vs "Complaint" (free text) — exact backend values
   * not yet observed on a real request; treat as an opaque display string. */
  requestType: string | null;
  complaintText: string | null;
  notes: string | null;
  isUrgent: boolean;
  requestedOn: string;
  requestStatus: string | null;
  assignedToUserId: number | null;
  assignedToUserName: string | null;
  assignedOn: string | null;
  startedOn: string | null;
  completedOn: string | null;
  cancelledOn: string | null;
  cancellationReason: string | null;
  createdOn: string;
  modifiedOn: string | null;
  items: GuestServiceRequestDetailDto[] | null;
  statusHistory: GuestServiceRequestStatusHistoryDto[] | null;
}

/** One row of the admin queue's combined feed. */
export interface GuestServiceFeedRowDto {
  source: string | null;
  id: number;
  referenceNumber: string | null;
  accountId: number;
  branchId: number;
  roomId: number;
  roomNo: string | null;
  guestName: string | null;
  directBookingId: number;
  roomBookingId: number | null;
  categoryId: number | null;
  categoryName: string | null;
  details: string | null;
  notes: string | null;
  isUrgent: boolean;
  requestedOn: string;
  status: string | null;
  displayStatus: string | null;
  assignedToUserId: number | null;
  assignedToUserName: string | null;
  canAssign: boolean;
}

export interface GuestServiceRequestSummaryDto {
  newRequests: number;
  inProgress: number;
  urgentOpen: number;
  completedToday: number;
  oldestNewRoomNo: string | null;
  oldestNewRequestedOn: string | null;
  asOfDate: string;
}

export interface UpdateGuestServiceRequestStatusDto {
  requestStatus: string | null;
  remarks: string | null;
  performedBy: number | null;
}

export interface AssignGuestServiceRequestDto {
  assignedToUserId: number | null;
  remarks: string | null;
  performedBy: number | null;
}

export interface CancelGuestServiceRequestDto {
  cancellationReason: string | null;
  performedBy: number | null;
}

export interface CancelGuestServiceRequestLineDto {
  reason: string | null;
  performedBy: number | null;
}

export interface GuestServiceRequestListFilters {
  accountId?: number;
  branchId?: number | null;
  directBookingId?: number;
  roomBookingId?: number;
  roomId?: number;
  guestServiceCategoryId?: number;
  requestStatus?: string;
  requestType?: string;
  isUrgent?: boolean;
  assignedToUserId?: number;
  fromDate?: string;
  toDate?: string;
}

export interface GuestServiceRequestFeedFilters {
  accountId?: number;
  branchId?: number | null;
  status?: string;
  categoryId?: number;
  source?: string;
  roomId?: number;
  isUrgent?: boolean;
  fromDate?: string;
  toDate?: string;
  search?: string;
  take?: number;
}

const buildQuery = (params: object): string => {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value !== undefined && value !== null) qs.set(key, String(value));
  }
  const str = qs.toString();
  return str ? `?${str}` : "";
};

// ------------------------------------------------------------- Guest-facing

export const getGuestServiceCatalogue = async (
  directBookingId: number,
  roomBookingId: number | null,
  // Same reasoning as createRoomRestaurantOrder's asAnonymousGuest: a staff
  // token sitting in localStorage from an unrelated tab must not leak into
  // this anonymous guest call — the backend's stay resolver treats a
  // present token's accountId claim as authoritative and would reject a
  // cross-account mismatch instead of ignoring it.
  { asAnonymousGuest = false }: { asAnonymousGuest?: boolean } = {}
): Promise<GuestServiceCatalogueDto> => {
  const res = await apiFetch(
    `/guestservicerequests/catalogue${buildQuery({ directBookingId, roomBookingId })}`,
    {},
    { skipAuth: asAnonymousGuest }
  );
  return res.json();
};

/** Returns one request per category present in the cart, same shape as
 * RoomRestaurantOrders' Create — {skipAuth: asAnonymousGuest} so the
 * no-login guest app is authorized by guestPhone, not a stray staff token. */
export const createGuestServiceRequest = async (
  payload: CreateGuestServiceRequestDto,
  { asAnonymousGuest = false }: { asAnonymousGuest?: boolean } = {}
): Promise<GuestServiceRequestDto[]> => {
  const res = await apiFetch(
    "/guestservicerequests",
    { method: "POST", body: JSON.stringify(payload) },
    { skipAuth: asAnonymousGuest }
  );
  return res.json();
};

export const createGuestServiceComplaint = async (
  payload: CreateGuestServiceComplaintDto,
  { asAnonymousGuest = false }: { asAnonymousGuest?: boolean } = {}
): Promise<GuestServiceRequestDto> => {
  const res = await apiFetch(
    "/guestservicerequests/complaint",
    { method: "POST", body: JSON.stringify(payload) },
    { skipAuth: asAnonymousGuest }
  );
  return res.json();
};

// ------------------------------------------------------------- Admin

export const listGuestServiceRequests = async (
  filters: GuestServiceRequestListFilters = {}
): Promise<GuestServiceRequestDto[]> => {
  const res = await apiFetch(`/guestservicerequests${buildQuery(filters)}`);
  return res.json();
};

/** The admin queue table's data source — both request "halves" (cart
 * requests and complaints) flattened into one row shape. */
export const listGuestServiceRequestsFeed = async (
  filters: GuestServiceRequestFeedFilters = {}
): Promise<GuestServiceFeedRowDto[]> => {
  const res = await apiFetch(`/guestservicerequests/feed${buildQuery(filters)}`);
  return res.json();
};

/** One page of the feed, filtered by the server, with the rows matching in all. */
export const listGuestServiceRequestsFeedPage = async (
  filters: GuestServiceRequestFeedFilters & { page: number; pageSize: number }
): Promise<{ items: GuestServiceFeedRowDto[]; total: number; page: number; pageSize: number }> => {
  const res = await apiFetch(`/guestservicerequests/feed/page${buildQuery(filters)}`);
  return res.json();
};

export const getGuestServiceRequestsSummary = async (params: {
  accountId?: number;
  branchId?: number | null;
  asOfDate?: string;
  utcOffsetMinutes?: number;
}): Promise<GuestServiceRequestSummaryDto> => {
  const res = await apiFetch(`/guestservicerequests/summary${buildQuery(params)}`);
  return res.json();
};

export const getGuestServiceRequest = async (
  id: number,
  accountId?: number
): Promise<GuestServiceRequestDto> => {
  const res = await apiFetch(
    `/guestservicerequests/${id}${buildQuery({ accountId })}`
  );
  return res.json();
};

export const updateGuestServiceRequestStatus = async (
  id: number,
  payload: UpdateGuestServiceRequestStatusDto,
  accountId?: number
): Promise<GuestServiceRequestDto> => {
  const res = await apiFetch(
    `/guestservicerequests/${id}/status${buildQuery({ accountId })}`,
    { method: "PATCH", body: JSON.stringify(payload) }
  );
  return res.json();
};

export const assignGuestServiceRequest = async (
  id: number,
  payload: AssignGuestServiceRequestDto,
  accountId?: number
): Promise<GuestServiceRequestDto> => {
  const res = await apiFetch(
    `/guestservicerequests/${id}/assign${buildQuery({ accountId })}`,
    { method: "POST", body: JSON.stringify(payload) }
  );
  return res.json();
};

export const cancelGuestServiceRequest = async (
  id: number,
  payload: CancelGuestServiceRequestDto,
  accountId?: number
): Promise<GuestServiceRequestDto> => {
  const res = await apiFetch(
    `/guestservicerequests/${id}/cancel${buildQuery({ accountId })}`,
    { method: "POST", body: JSON.stringify(payload) }
  );
  return res.json();
};

export const cancelGuestServiceRequestLine = async (
  id: number,
  requestDetailId: number,
  payload: CancelGuestServiceRequestLineDto,
  accountId?: number
): Promise<GuestServiceRequestDto> => {
  const res = await apiFetch(
    `/guestservicerequests/${id}/items/${requestDetailId}/cancel${buildQuery({ accountId })}`,
    { method: "POST", body: JSON.stringify(payload) }
  );
  return res.json();
};
